import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Upload,
  FileText,
  CheckCircle2,
  Loader2,
  Briefcase,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Edit,
  Sparkles,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import ATSAnalysis from "../components/ATSAnalysis";
import PrivacyPanel from "../components/PrivacyPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CandidateProfile() {
  const navigate = useNavigate();
  const { user, requireEmail, setShowEmailCapture } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    location: "",
    title: "",
    summary: "",
    linkedin_url: "",
  });

  useEffect(() => {
    if (user?.email) {
      fetchProfile();
      fetchApplications();
    } else {
      setLoading(false);
    }
  }, [user?.email]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/candidate/profile/${encodeURIComponent(user.email)}`);
      setProfile(response.data);
      setFormData({
        name: response.data.name || "",
        phone: response.data.phone || "",
        location: response.data.location || "",
        title: response.data.title || "",
        summary: response.data.summary || "",
        linkedin_url: response.data.linkedin_url || "",
      });
    } catch (error) {
      // Profile doesn't exist yet
      setEditMode(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/candidate/applications/${encodeURIComponent(user.email)}`);
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  const handleSaveProfile = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/candidate/profile`, {
        ...formData,
        email: user.email,
      });
      setProfile(response.data);
      setEditMode(false);
      toast.success("Perfil guardado exitosamente");
    } catch (error) {
      toast.error("Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Solo se permiten archivos PDF o DOCX");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/candidate/upload-cv?email=${encodeURIComponent(user.email)}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("CV subido y analizado exitosamente");
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir el CV");
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <Card className="text-center py-12">
          <CardContent>
            <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">Crea tu perfil de candidato</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Ingresa tu correo para crear tu perfil, subir tu CV y empezar a aplicar a ofertas de trabajo
            </p>
            <Button onClick={() => setShowEmailCapture(true)} className="rounded-full">
              Comenzar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="candidate-profile">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/empleos")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              Mi Perfil
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestiona tu información, CV y privacidad
            </p>
          </div>
        </div>
        {profile && !editMode && (
          <Button variant="outline" onClick={() => setEditMode(true)} className="gap-2">
            <Edit className="w-4 h-4" />
            Editar
          </Button>
        )}
      </div>

      {/* Tabs for Profile and Privacy */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="profile" className="gap-2">
            <User className="w-4 h-4" />
            Mi Perfil
          </TabsTrigger>
          <TabsTrigger value="privacy" className="gap-2">
            <Shield className="w-4 h-4" />
            Privacidad
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="space-y-6 mt-6">
          {/* Profile Card */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5" />
                Información Personal
              </CardTitle>
              <CardDescription>
            Esta información será visible para los reclutadores cuando apliques
          </CardDescription>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <Label htmlFor="title">Título profesional</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="ej: Desarrollador Full Stack"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Ubicación</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Ciudad, País"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/tu-perfil"
                />
              </div>

              <div>
                <Label htmlFor="summary">Resumen profesional</Label>
                <Textarea
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Breve descripción de tu experiencia y objetivos..."
                  rows={4}
                />
              </div>

              <div className="flex gap-2 justify-end">
                {profile && (
                  <Button variant="outline" onClick={() => setEditMode(false)}>
                    Cancelar
                  </Button>
                )}
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Guardar
                </Button>
              </div>
            </div>
          ) : profile ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-heading font-bold text-primary">
                    {profile.name?.charAt(0) || "?"}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold">{profile.name}</h3>
                  {profile.title && (
                    <p className="text-muted-foreground">{profile.title}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </div>
                {profile.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    {profile.phone}
                  </div>
                )}
                {profile.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    {profile.location}
                  </div>
                )}
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                )}
              </div>

              {profile.summary && (
                <p className="text-muted-foreground text-sm border-t pt-4">
                  {profile.summary}
                </p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* CV Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Mi CV
          </CardTitle>
          <CardDescription>
            Sube tu CV para poder aplicar a ofertas y recibir optimizaciones con IA
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload Zone */}
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                className="hidden"
                id="cv-upload"
                disabled={uploading}
              />
              <label htmlFor="cv-upload" className="cursor-pointer">
                {uploading ? (
                  <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
                ) : (
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                )}
                <p className="font-medium">
                  {uploading ? "Analizando CV..." : "Subir CV"}
                </p>
                <p className="text-sm text-muted-foreground">
                  PDF o DOCX (máx. 5MB)
                </p>
              </label>
            </div>

            {/* CV Data Display */}
            {profile?.cv_data && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">CV analizado correctamente</span>
                </div>

                {profile.skills?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Habilidades detectadas</h4>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.slice(0, 10).map((skill, i) => (
                        <Badge key={i} variant="secondary">{skill}</Badge>
                      ))}
                      {profile.skills.length > 10 && (
                        <Badge variant="outline">+{profile.skills.length - 10} más</Badge>
                      )}
                    </div>
                  </div>
                )}

                {profile.education?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Educación</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {profile.education.map((edu, i) => (
                        <li key={i}>• {edu}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {profile.years_experience > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Años de experiencia:</span>
                    <Badge>{profile.years_experience} años</Badge>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ATS Compatibility Analysis - Only show if CV is uploaded */}
      {profile?.cv_data && (
        <ATSAnalysis 
          userEmail={user.email}
          userPlan={user.plan}
        />
      )}

      {/* Applications */}
      {applications.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Mis Aplicaciones ({applications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {applications.map((app) => (
                <div
                  key={app.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div>
                    <p className="font-medium">{app.job?.title || "Puesto"}</p>
                    <p className="text-xs text-muted-foreground">
                      Aplicado el {new Date(app.applied_at).toLocaleDateString("es-ES")}
                    </p>
                  </div>
                  <Badge className={
                    app.status === "accepted" ? "bg-green-100 text-green-700" :
                    app.status === "rejected" ? "bg-red-100 text-red-700" :
                    app.status === "reviewed" ? "bg-yellow-100 text-yellow-700" :
                    "bg-gray-100 text-gray-700"
                  }>
                    {app.status === "pending" ? "Pendiente" :
                     app.status === "reviewed" ? "En revisión" :
                     app.status === "accepted" ? "Aceptado" : "Rechazado"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CTA to browse jobs */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-primary" />
              <div>
                <h3 className="font-heading font-semibold">¿Listo para aplicar?</h3>
                <p className="text-sm text-muted-foreground">
                  Explora ofertas de trabajo y optimiza tu CV para cada una
                </p>
              </div>
            </div>
            <Link to="/empleos">
              <Button className="rounded-full">
                Ver ofertas de empleo
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Privacy Tab */}
        <TabsContent value="privacy" className="mt-6">
          <PrivacyPanel 
            userEmail={user.email}
            userPlan={user.plan}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
